import { useState, useCallback } from "react";
import { Box, Paper, Chip, Tooltip, Typography, CircularProgress } from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams, GridComparatorFn } from "@mui/x-data-grid";
import { Search20Regular } from "@fluentui/react-icons";
import { CustomDataGridToolbar } from "../../SharedComponents/CustomDataGrid/CustomDataGridToolbar";
import { formatDisplayDate } from "../../Utils/js-common";
import { IFeatureRequestSummary } from "../../interfaces/CloseTheLoop.interfaces";
import { logEvent } from "../../helper/AppInsightsHelper";
import {
  THEME,
  StatusChip,
  PriorityBadge,
  FeatureDetailsPanel
} from "../../pages/CloseTheLoopPage";

/**
 * Customer View - Feature Requests table.
 *
 * Replicates the columns, toolbar and design of the Close the Loop "All Submissions"
 * grid, but operates client-side over the feature requests belonging to a single
 * customer (already filtered by tenantId upstream). Clicking a Feature Request ID
 * opens the shared FeatureDetailsPanel.
 */
export default function FeatureTable(props: {
  readonly rows: IFeatureRequestSummary[];
  readonly loading: boolean;
  readonly tenantName?: string;
}) {
  const { rows, loading } = props;

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [isExporting, setIsExporting] = useState(false);

  // Details panel state
  const [selectedFeatureId, setSelectedFeatureId] = useState<string | null>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const handleOpenDetails = useCallback((featureRequestIdWithSource: string) => {
    setSelectedFeatureId(featureRequestIdWithSource);
    setIsPanelOpen(true);
    logEvent("Feature Request details opened.", "Customer Page", "CustomerFeatureRequestsTable", {
      tenantName: props.tenantName,
      featureRequestIdWithSource
    });
  }, [props.tenantName]);

  const handleClosePanel = useCallback(() => {
    setIsPanelOpen(false);
    setSelectedFeatureId(null);
  }, []);

  // Client-side search across the primary text fields
  const formatDate = formatDisplayDate;

  // Parse a raw date value into a sortable timestamp. Handles ISO strings and the
  // dd-MM-yyyy format (mirrors formatDisplayDate); unparseable/empty sort to the bottom.
  const toSortableTime = (value?: string | null): number => {
    if (!value) return -Infinity;
    const ddMMyyyy = value.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
    if (ddMMyyyy) {
      const [, day, month, year] = ddMMyyyy;
      return new Date(Number(year), Number(month) - 1, Number(day)).getTime();
    }
    const time = new Date(value).getTime();
    return isNaN(time) ? -Infinity : time;
  };

  const dateSortComparator = (field: keyof IFeatureRequestSummary): GridComparatorFn =>
    (_v1, _v2, p1, p2) =>
      toSortableTime(p1.api.getRow(p1.id)?.[field] as string | null | undefined) -
      toSortableTime(p2.api.getRow(p2.id)?.[field] as string | null | undefined);

  const columns: GridColDef[] = [
    {
      field: "featureRequestId",
      headerName: "Feature Request ID",
      width: 160,
      renderCell: (params: GridRenderCellParams) => (
        <Box
          component="span"
          role="button"
          tabIndex={0}
          aria-label={`Open feature request details for ${params.value}`}
          sx={{
            color: THEME.primary,
            fontWeight: 500,
            fontSize: "12px",
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" }
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleOpenDetails((params.row as IFeatureRequestSummary).featureRequestIdWithSource);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
              handleOpenDetails((params.row as IFeatureRequestSummary).featureRequestIdWithSource);
            }
          }}
        >
          {params.value}
        </Box>
      )
    },
    {
      field: "title",
      headerName: "Title",
      flex: 1,
      minWidth: 250,
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title={params.value || ""}>
          <Box
            component="span"
            sx={{
              fontSize: "12px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }}
          >
            {params.value}
          </Box>
        </Tooltip>
      )
    },
    {
      field: "productName",
      headerName: "Product",
      width: 130,
      renderCell: (params: GridRenderCellParams) => (
        <Box component="span" sx={{ fontSize: "12px" }}>
          {params.value || "—"}
        </Box>
      )
    },
    {
      field: "customerCount",
      headerName: "No of Customers",
      width: 120,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value || 0}
          size="small"
          sx={{
            backgroundColor: params.value > 1 ? "#E8F5E9" : "#F5F5F5",
            color: params.value > 1 ? THEME.success : THEME.textSecondary,
            fontWeight: 600,
            fontSize: "11px",
            height: "22px",
            minWidth: "36px"
          }}
        />
      )
    },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      renderCell: (params: GridRenderCellParams) => <StatusChip status={params.value || ""} />
    },
    {
      field: "customerFacingStatus",
      headerName: "Customer Facing Status",
      width: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title={params.value || ""}>
          <Box
            component="span"
            sx={{
              fontSize: "12px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              color: params.value ? THEME.textPrimary : THEME.textSecondary
            }}
          >
            {params.value || "—"}
          </Box>
        </Tooltip>
      )
    },
    {
      field: "priority",
      headerName: "Priority",
      width: 80,
      renderCell: (params: GridRenderCellParams) => <PriorityBadge priority={params.value} />
    },
    {
      field: "source",
      headerName: "Source",
      width: 80,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value || "—"}
          size="small"
          sx={{
            backgroundColor: params.value === "CRM" ? "#E5F1FB" :
              params.value === "UAT" ? "#E8F5E9" :
                params.value === "CSS" ? "#FFF3E0" : "#F5F5F5",
            color: params.value === "CRM" ? THEME.primary :
              params.value === "UAT" ? THEME.success :
                params.value === "CSS" ? "#E65100" : THEME.textSecondary,
            fontWeight: 500,
            fontSize: "11px",
            height: "22px"
          }}
        />
      )
    },
    {
      field: "latestCreatedDate",
      headerName: "Created",
      width: 120,
      valueGetter: (_value, row) => formatDate(row.latestCreatedDate),
      sortComparator: dateSortComparator("latestCreatedDate"),
      renderCell: (params: GridRenderCellParams) => (
        <Box
          component="span"
          sx={{
            fontSize: "12px",
            color: params.value && params.value !== "—" ? THEME.textPrimary : THEME.textSecondary
          }}
        >
          {params.value || "—"}
        </Box>
      )
    },
    {
      field: "lastChangedDate",
      headerName: "Last Changed",
      width: 120,
      valueGetter: (_value, row) => formatDate(row.lastChangedDate),
      sortComparator: dateSortComparator("lastChangedDate"),
      renderCell: (params: GridRenderCellParams) => (
        <Box
          component="span"
          sx={{
            fontSize: "12px",
            color: params.value && params.value !== "—" ? THEME.textPrimary : THEME.textSecondary
          }}
        >
          {params.value || "—"}
        </Box>
      )
    }
  ];

  // Export rows to CSV (mirrors All Submissions export)
  const handleExportAll = useCallback(async () => {
    setIsExporting(true);
    try {
      const headers = [
        "Feature Request ID",
        "Title",
        "Product",
        "Status",
        "Priority",
        "Source",
        "Customer",
        "Evidence Count",
        "Customer Count",
        "Created Date",
        "Last Changed Date",
        "Engineering PM",
        "Customer Facing Status",
        "Feature Category"
      ];
      const csvRows = [headers.join(",")];
      rows.forEach((row) => {
        const values = [
          `"${(row.featureRequestId || "").replace(/"/g, "\"\"")}"`,
          `"${(row.title || "").replace(/"/g, "\"\"")}"`,
          `"${(row.productName || "").replace(/"/g, "\"\"")}"`,
          `"${(row.status || "").replace(/"/g, "\"\"")}"`,
          `"${(row.priority || "").replace(/"/g, "\"\"")}"`,
          `"${(row.source || "").replace(/"/g, "\"\"")}"`,
          `"${(row.customerDisplay || "").replace(/"/g, "\"\"")}"`,
          row.evidenceCount || 0,
          row.customerCount || 0,
          `"${(row.latestCreatedDate || "").replace(/"/g, "\"\"")}"`,
          `"${(row.lastChangedDate || "").replace(/"/g, "\"\"")}"`,
          `"${(row.engineeringPM || "").replace(/"/g, "\"\"")}"`,
          `"${(row.customerFacingStatus || "").replace(/"/g, "\"\"")}"`,
          `"${(row.featureCategory || "").replace(/"/g, "\"\"")}"`
        ];
        csvRows.push(values.join(","));
      });
      const csvContent = csvRows.join("\n");
      const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `feature-requests-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setIsExporting(false);
    }
  }, [rows]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, height: "100%" }}>
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${THEME.border}`,
          borderRadius: "8px",
          overflow: "hidden",
          flex: 1,
          minHeight: 200,
          maxHeight: "calc(100vh - 380px)",
          display: "flex",
          flexDirection: "column",
          "& .MuiDataGrid-root": {
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            border: "none"
          },
          "& .MuiDataGrid-main": {
            flex: 1,
            overflow: "auto",
            minHeight: 0
          },
          "& .MuiDataGrid-footerContainer": {
            minHeight: 52,
            flexShrink: 0,
            borderTop: `1px solid ${THEME.border}`
          }
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          density="compact"
          getRowId={(row) => row.featureRequestIdWithSource}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[25, 50, 100]}
          initialState={{
            sorting: { sortModel: [{ field: "lastChangedDate", sort: "desc" }] }
          }}
          disableRowSelectionOnClick
          slots={{
            toolbar: () => (
              <CustomDataGridToolbar
                filterModel={{ items: [] }}
                toolbarConfig={{ disableGridToolbarQuickFilter: false }}
                exportCsvButtonProps={{
                  buttonLabel: isExporting ? "Exporting..." : "Export All",
                  fileNamePrefix: "feature-requests",
                  tooltip: "Export all data to CSV",
                  onCustomExport: handleExportAll,
                  isExporting: isExporting
                }}
              />
            ),
            loadingOverlay: () => (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  width: "100%",
                  backgroundColor: "rgba(255, 255, 255, 0.95)"
                }}
              >
                <CircularProgress size={40} sx={{ color: THEME.primary, mb: 2 }} />
                <Typography sx={{ color: THEME.textPrimary, fontSize: "14px", fontWeight: 500 }}>
                  Loading data...
                </Typography>
              </Box>
            ),
            noRowsOverlay: () => (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  py: 4,
                  background: "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(250,251,252,1) 100%)"
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", maxWidth: 320 }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: "20px",
                      background: "linear-gradient(135deg, #F0F4FF 0%, #E8F0FE 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 3,
                      boxShadow: "0 4px 12px rgba(0, 90, 158, 0.08)"
                    }}
                  >
                    <Search20Regular style={{ color: THEME.primary, width: 36, height: 36, opacity: 0.8 }} />
                  </Box>
                  <Typography sx={{ color: THEME.textPrimary, fontSize: "16px", fontWeight: 600, mb: 1 }}>
                    No Results Found
                  </Typography>
                  <Typography sx={{ color: THEME.textSecondary, fontSize: "13px", textAlign: "center", lineHeight: 1.5 }}>
                    We couldn't find any feature requests matching your criteria. Try adjusting your search terms.
                  </Typography>
                </Box>
              </Box>
            )
          }}
          sx={{
            border: "none",
            fontSize: "12px",
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#F8F9FA",
              borderBottom: `1px solid ${THEME.border}`
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontSize: "12px",
              fontWeight: 600
            },
            "& .MuiDataGrid-cell": {
              borderBottom: `1px solid ${THEME.border}`,
              fontSize: "12px"
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: `${THEME.primary}08`
            },
            "& .MuiDataGrid-menuIconButton": { color: "white" },
            "& .MuiDataGrid-overlayWrapper": {
              backgroundColor: "rgba(255, 255, 255, 0.98)"
            }
          }}
        />
      </Paper>

      <FeatureDetailsPanel
        isOpen={isPanelOpen}
        onClose={handleClosePanel}
        featureRequestIdWithSource={selectedFeatureId}
        showCustomersSection={true}
      />
    </Box>
  );
}
